import { useMutation, useQueryClient } from '@tanstack/react-query';

import { payrunQueryKeys } from '@xemplo/gp-constants';
import {
  CommonPayrun,
  FinalisePayrunRequestModel,
  PayrunApproverCreate,
  PayrunApproverUpdate,
  PreparePayrunCreate,
  SkipPayrun,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import {
  CreatePayrunApproverProps,
  CreatePreparePayrunProps,
  DeletePayrunApproverProps,
  RejectEarningLinesPayload,
  UpdatePayrunApproverProps,
  UpdatePayrunAssignee,
  UpdatePayrunMilestonesPayload,
  UseFinalisePayrunProps,
  UseRejectEarningLinesProps,
  UseUpdatePayrunAssignee,
} from '../payrun-query.types';

import { PayrunAPIV2 } from './payrun-query.constants';

/** Add new Payrun approver*/
export const useCreatePayrunApprovers = (props: CreatePayrunApproverProps) => {
  const { onSuccess, onError, companyId } = props;

  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(PayrunAPIV2.payrunApprovers(companyId), applicationApiUrl);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PayrunApproverCreate[]) =>
      client.post(url, payload).then((resp) => resp.data),
    onSuccess: (data, variants, context) => {
      queryClient.invalidateQueries({
        queryKey: payrunQueryKeys.approvers.lists(companyId),
      });
      onSuccess?.(data, variants, context);
    },
    onError,
  });
};

/** Remove Payrun approver for user*/
export const useDeletePayrunApprovers = (props: DeletePayrunApproverProps) => {
  const { onSuccess, onError } = props;

  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CommonPayrun) => {
      const { companyId, userId } = payload;
      const url = apiUrlBuilder(
        PayrunAPIV2.payrunApproversDetails(companyId ?? null, userId),
        applicationApiUrl,
      );

      const resp = await client.delete<CommonPayrun>(url);
      return resp.data;
    },
    onSuccess: (data, variants, context) => {
      const companyId = data.companyId ?? '';
      queryClient.invalidateQueries({
        queryKey: payrunQueryKeys.approvers.lists(companyId),
      });
      onSuccess?.(data, variants, context);
    },
    onError,
  });
};

/** Skip Payruns */
export const useSkipPayruns = () => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(PayrunAPIV2.skipPayRunsV2, applicationApiUrl);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SkipPayrun) =>
      client.put(url, payload).then((resp) => resp.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: payrunQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: payrunQueryKeys.details(),
      });
    },
  });
};

/** Update Payrun Approver*/
export const useUpdatePayrunApprovers = (props: UpdatePayrunApproverProps) => {
  const { onSuccess, onError } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PayrunApproverUpdate) => {
      const { companyId, userId } = payload;
      const url = apiUrlBuilder(
        PayrunAPIV2.payrunApproversDetails(companyId, userId),
        applicationApiUrl,
      );
      return client.patch(url, payload).then((resp) => resp.data);
    },
    onSuccess: (data: unknown, variants: PayrunApproverUpdate) => {
      const { companyId } = variants;
      queryClient.invalidateQueries({
        queryKey: payrunQueryKeys.approvers.lists(companyId),
      });
      queryClient.invalidateQueries({
        queryKey: payrunQueryKeys.approvers.details(companyId),
      });
      onSuccess?.();
    },
    onError,
  });
};

/** Reject payrun during review stage  */
export const useRejectEarningLines = ({
  onSuccess,
  onError,
  payrunId,
}: UseRejectEarningLinesProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(
    PayrunAPIV2.rejectEarningLinesByPayrunId(payrunId),
    applicationApiUrl,
  );
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RejectEarningLinesPayload) => {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value) {
          const parsedValue = value instanceof Blob ? value : value.toString();
          formData.append(key, parsedValue);
        }
      });
      return await client.post(url, formData).then((resp) => resp.data);
    },
    onSuccess: (data, variables: RejectEarningLinesPayload, onMutateResult, context) => {
      onSuccess?.(data, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: payrunQueryKeys.details() });
    },
    onError,
  });
};

/** Prepare Payrun */
export const usePreparePayrun = (props: CreatePreparePayrunProps) => {
  const { payrunId, onSuccess, onError } = props;

  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();

  return useMutation({
    mutationFn: async (payload: PreparePayrunCreate) => {
      const url = apiUrlBuilder(PayrunAPIV2.preparePayrun(payrunId), applicationApiUrl);
      const resp = await client.post(url, payload);
      return resp.data;
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError,
  });
};

export const useUpdatePayrunAssignee = (props: UseUpdatePayrunAssignee) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();

  return useMutation({
    mutationFn: async (payload: UpdatePayrunAssignee) => {
      const { payrunId, ...rest } = payload;
      const url = apiUrlBuilder(PayrunAPIV2.updateAssignee(payrunId), applicationApiUrl);
      const resp = await client.patch(url, rest);
      return resp.data;
    },
    ...props,
  });
};

export const useUpdatePayrunMilestones = () => {
  const { applicationApiUrl } = useAppSettings();
  const queryClient = useQueryClient();
  const { client } = useHttpClient();

  return useMutation({
    mutationFn: async (payload: UpdatePayrunMilestonesPayload) => {
      const url = apiUrlBuilder(PayrunAPIV2.updatePayrunMilestones, applicationApiUrl);
      const resp = await client.patch(url, payload);
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: payrunQueryKeys.details(),
      });
      //TODO: invalidate get milestones
    },
  });
};

/**
 * Finalise a pay run: posts the finalise-external-payrun / send-payslips flags and, on
 * success, refreshes the pay run details so its status reflects the finalisation.
 * See docs/adr/0008.
 */
export const useFinalisePayrun = (props: UseFinalisePayrunProps) => {
  const { payrunId, onSuccess, onError } = props;

  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(PayrunAPIV2.finalisePayrun(payrunId), applicationApiUrl);

  return useMutation({
    mutationFn: (payload: FinalisePayrunRequestModel) =>
      client.post(url, payload).then((resp) => resp.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payrunQueryKeys.details() });
      queryClient.invalidateQueries({ queryKey: payrunQueryKeys.lists() });
      onSuccess?.();
    },
    ...(onError && { onError }),
  });
};
